import { z } from 'zod'

import { timestampSchema } from '@/utility'

const projectIconDefinitionSchema = z
  .object({
    /** Key to local icon OR URL to hosted icon */
    src: z.string().default(''),
    /** Image alt or svg title */
    title: z.string().optional(),
    /** Mask a non-svg image */
    mask: z.boolean().optional(),
    /** Search and group tags */
    tags: z.string().array().default([]),
    /** Sorting group in list */
    group: z.string().default(''),
  })
  .meta({ id: 'project-icon-definition' })

/**
 * Project Icon Definition
 *
 * Contains the information required to list an icon for user selection
 */
export type ProjectIconDefinition = z.infer<typeof projectIconDefinitionSchema>

// ---------------------------------------------------------------------------

export const customProjectIconSchema = projectIconDefinitionSchema
  .extend({
    /** Timestamp for when the icon was uploaded */
    createdAt: timestampSchema,
    /** User ID who uploaded the icon */
    uploadedBy: z.string(),
    /** Always a custom icon */
    group: z.literal('custom'),
  })
  .meta({ id: 'custom-project-icon' })

/**
 * Custom User Uploaded Icon Definition
 *
 * A superset of the normal icon definition that also contains
 * the information about a user uploaded icon
 */
export type CustomProjectIconDefinition = z.infer<
  typeof customProjectIconSchema
>

// ---------------------------------------------------------------------------

/**
 * Simplified Icon Definition
 *
 * Contains the information required to **render** a user selected icon
 */
export type ProjectIconInfo = {
  src: string
  title?: string
  mask?: boolean
}

const projectIconInfoSchema = z
  .object({
    src: z.string().default(''),
    title: z.string().optional(),
    mask: z.boolean().optional(),
  })
  .meta({ id: 'project-icon-info' }) satisfies z.ZodType<ProjectIconInfo>

export const projectIconSchema = z
  .preprocess((arg: any) => {
    if (!arg) return arg

    if (!arg.src) arg.src = arg.local || arg.remote
    if (arg.local) arg.group = 'solid'
    if (arg.remote) arg.group = 'custom'

    return arg
  }, projectIconInfoSchema)
  .meta({ id: 'project-icon' }) satisfies z.ZodType<ProjectIconInfo>
